import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../Spinner';

export default function CategoryStatsTable() {
  const { token } = useAuth();
  const [stats, setStats] = useState({ categories: [], top_hikes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/admin/stats/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Errore nel caricamento delle statistiche');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const getTotalViews = () => {
    return stats.categories.reduce((sum, cat) => sum + (cat.total_views || 0), 0);
  };

  const getTotalHikes = () => {
    return stats.categories.reduce((sum, cat) => sum + (cat.hike_count || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500 uppercase">Visualizzazioni Totali</div>
          <div className="mt-2 text-3xl font-bold text-blue-600">{getTotalViews().toLocaleString()}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500 uppercase">Categorie Attive</div>
          <div className="mt-2 text-3xl font-bold text-green-600">{stats.categories.length}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500 uppercase">Gite con Categoria</div>
          <div className="mt-2 text-3xl font-bold text-purple-600">{getTotalHikes()}</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Category Stats Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Visualizzazioni per Categoria</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Categoria</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Visualizzazioni</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">N. Gite</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Media per Gita</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Distribuzione</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stats.categories.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    Nessuna categoria trovata
                  </td>
                </tr>
              ) : (
                stats.categories.map((cat, index) => {
                  const percentage = getTotalViews() > 0
                    ? ((cat.total_views / getTotalViews()) * 100).toFixed(1)
                    : 0;
                  const avgViews = cat.hike_count > 0
                    ? Math.round(cat.total_views / cat.hike_count)
                    : 0;

                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {cat.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        {(cat.total_views || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-600">
                        {cat.hike_count}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-600">
                        {avgViews.toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 w-12">{percentage}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Hikes Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Gite Più Visualizzate</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Titolo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Luogo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Data</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Visualizzazioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stats.top_hikes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    Nessuna gita trovata
                  </td>
                </tr>
              ) : (
                stats.top_hikes.map((hike, index) => (
                  <tr key={hike.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">{index + 1}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{hike.titolo}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{hike.luogo}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {hike.data_inizio ? new Date(hike.data_inizio).toLocaleDateString('it-IT') : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {(hike.view_count || 0).toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="text-center">
        <button
          onClick={fetchStats}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Caricamento...' : 'Aggiorna statistiche'}
        </button>
      </div>
    </div>
  );
}
